const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');

// const SEEDS_API_BASE =
//     typeof window !== 'undefined' && window.SEEDS_API_BASE ?
//         window.SEEDS_API_BASE :
//         'http://localhost:3000';

/* global process */
const SEEDS_API_BASE = process.env.SEEDS_API_BASE || 'http://localhost:3000';


class Scratch3SeedsAI {
    constructor (runtime) {
        this.runtime = runtime;
        this._authToken = null;
        this._classId = null;
        this._imageModels = [];
        this._textModels = [];
        this._loadedClassifiers = {};

        this._initAuthFromURL();
        this._loadModels();
    }

    _initAuthFromURL () {
        try {
            if (typeof window !== 'undefined') {
                const params = new URLSearchParams(window.location.search);
                this._authToken = params.get('token');
                this._classId = params.get('classId');
            }
        } catch (e) {
            console.warn('SEEDS AI: Could not read URL parameters', e);
        }
    }

    _getAuthHeaders (contentType = null) {
        const headers = {};
        if (this._authToken) {
            headers.Authorization = `Bearer ${this._authToken}`;
        }
        if (contentType) {
            headers['Content-Type'] = contentType;
        }
        return headers;
    }

    _buildUrl (baseUrl) {
        const url = new URL(baseUrl, SEEDS_API_BASE);
        if (this._classId) {
            url.searchParams.set('classId', this._classId);
        }
        return url.toString();
    }

    async _loadModels () {
        try {
            const imageUrl = this._buildUrl('/api/tools/image-classifier/models');
            const imageResponse = await fetch(imageUrl, {
                headers: this._getAuthHeaders()
            });
            if (imageResponse.ok) {
                const data = await imageResponse.json();
                if (data.success && data.models) {
                    this._imageModels = data.models;
                }
            }
        } catch (e) {
            console.warn('SEEDS AI: Could not load image models', e);
        }

        try {
            const textUrl = this._buildUrl('/api/tools/text-classifier/models');
            const textResponse = await fetch(textUrl, {
                headers: this._getAuthHeaders()
            });
            if (textResponse.ok) {
                const data = await textResponse.json();
                if (data.success && data.models) this._textModels = data.models;
            }
        } catch (e) {
            console.warn('SEEDS AI: Could not load text models', e);
        }
    }

    getInfo () {
        return {
            id: 'seedsAI',
            name: 'AI Tools',
            color1: '#FF6B35',
            color2: '#E55A2B',
            blocks: [
                {
                    opcode: 'classifyImage',
                    blockType: BlockType.REPORTER,
                    text: 'classify image [IMAGE_URL] with [MODEL] get [OUTPUT_TYPE]',
                    arguments: {
                        IMAGE_URL: {
                            type: ArgumentType.STRING,
                            defaultValue: 'https://example.com/image.jpg'
                        },
                        MODEL: {
                            type: ArgumentType.STRING,
                            menu: 'imageModelMenu',
                            defaultValue: ''
                        },
                        OUTPUT_TYPE: {
                            type: ArgumentType.STRING,
                            menu: 'outputTypeMenu',
                            defaultValue: 'prediction'
                        }
                    }
                },
                {
                    opcode: 'classifyText',
                    blockType: BlockType.REPORTER,
                    text: 'classify text [TEXT] with [MODEL] get [OUTPUT_TYPE]',
                    arguments: {
                        TEXT: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Hello world'
                        },
                        MODEL: {
                            type: ArgumentType.STRING,
                            menu: 'textModelMenu',
                            defaultValue: ''
                        },
                        OUTPUT_TYPE: {
                            type: ArgumentType.STRING,
                            menu: 'outputTypeMenu',
                            defaultValue: 'prediction'
                        }
                    }
                }
            ],
            menus: {
                outputTypeMenu: {
                    acceptReporters: false,
                    items: [
                        {text: 'prediction', value: 'prediction'},
                        {text: 'confidence', value: 'confidence'},
                        {text: 'prediction + confidence', value: 'both'},
                        {text: 'all probabilities', value: 'all'}
                    ]
                },
                imageModelMenu: {
                    acceptReporters: false,
                    items: 'getImageModelMenuItems'
                },
                textModelMenu: {
                    acceptReporters: false,
                    items: 'getTextModelMenuItems'
                }
            }
        };
    }

    getImageModelMenuItems () {
        if (this._imageModels.length === 0) {
            return [{text: 'No models', value: ''}];
        }
        return this._imageModels.map(m => ({
            text: m.modelName,
            value: m.modelName
        }));
    }

    getTextModelMenuItems () {
        if (this._textModels.length === 0) {
            return [{text: 'No models', value: ''}];
        }
        return this._textModels.map(m => ({
            text: m.modelName,
            value: m.modelName
        }));
    }

    async _loadClassifier (modelName, modelType) {
        const cacheKey = `${modelType}:${modelName}`;
        if (this._loadedClassifiers[cacheKey]) {
            return this._loadedClassifiers[cacheKey];
        }

        const endpoint =
            modelType === 'text-classifier' ?
                '/api/blockly/load-text-classifier' :
                '/api/blockly/load-classifier';

        const response = await fetch(this._buildUrl(endpoint), {
            method: 'POST',
            headers: this._getAuthHeaders('application/json'),
            body: JSON.stringify({modelName, modelType})
        });

        if (!response.ok) throw new Error(`Failed to load model: ${modelName}`);
        const result = await response.json();
        if (!result.success) {
            throw new Error(result.error || 'Failed to load model');
        }

        this._loadedClassifiers[cacheKey] = result;
        return result;
    }

    async classifyImage (args) {
        const imageUrl = String(args.IMAGE_URL);
        const modelName = String(args.MODEL);
        const outputType = String(args.OUTPUT_TYPE);

        if (!modelName) return 'No model selected';

        try {
            const classifier = await this._loadClassifier(
                modelName,
                'image-classifier'
            );
            const response = await fetch(
                this._buildUrl('/api/blockly/classify-image'),
                {
                    method: 'POST',
                    headers: this._getAuthHeaders('application/json'),
                    body: JSON.stringify({
                        modelId: classifier.modelId,
                        imageUrl
                    })
                }
            );

            if (!response.ok) return 'Classification failed';
            const results = await response.json();
            if (!results.success || !results.predictions?.length) {
                return 'No predictions';
            }

            const top = results.predictions[0];
            switch (outputType) {
            case 'prediction':
                return top.className;
            case 'confidence':
                return Math.round(top.probability * 100);
            case 'both':
                return `${top.className} (${Math.round(
                    top.probability * 100
                )}%)`;
            case 'all':
                return JSON.stringify(results.predictions);
            default:
                return top.className;
            }
        } catch (error) {
            return `Error: ${error.message}`;
        }
    }

    async classifyText (args) {
        const text = String(args.TEXT);
        const modelName = String(args.MODEL);
        const outputType = String(args.OUTPUT_TYPE);

        if (!modelName) return 'No model selected';

        try {
            const classifier = await this._loadClassifier(
                modelName,
                'text-classifier'
            );
            const response = await fetch(
                `${SEEDS_API_BASE}/api/tools/text-classifier/predict`,
                {
                    method: 'POST',
                    headers: this._getAuthHeaders('application/json'),
                    body: JSON.stringify({modelId: classifier.modelId, text})
                }
            );

            if (!response.ok) return 'Classification failed';
            const results = await response.json();
            if (!results.success) return 'No predictions';

            switch (outputType) {
            case 'prediction':
                return results.prediction;
            case 'confidence':
                return Math.round(results.confidence * 100);
            case 'both':
                return `${results.prediction} (${Math.round(
                    results.confidence * 100
                )}%)`;
            case 'all':
                if (results.probabilities) {
                    return Object.entries(results.probabilities)
                        .map(([c, p]) => `${c}: ${Math.round(p * 100)}%`)
                        .join(', ');
                }
                return 'No probabilities';
            default:
                return results.prediction;
            }
        } catch (error) {
            return `Error: ${error.message}`;
        }
    }
}

module.exports = Scratch3SeedsAI;
